import React, { useState, useEffect } from 'react';
import { DropdownItem } from 'reactstrap';
import { useIntl } from 'react-intl';
import API from '../../services/API.redux';
import { applicationActions } from '../../new.redux/actions';
import { store } from '../../redux/storeConfig/store';
import { FaDollarSign } from 'react-icons/fa';

const CurrencyDropdownMenu = ({ userData, onCurrencyChange }) => {
  const [currencies, setCurrencies] = useState([]);
  const [currentSymbol, setCurrentSymbol] = useState('$');
  const intl = useIntl();

  // Definir moeda principal para cada símbolo (prioridade)
  const SYMBOL_PRIORITY = {
    '$': 'USD',      // Dólar Americano como principal para $
    '¥': 'JPY',      // Iene Japonês como principal para ¥
    'C$': 'CAD',     // Dólar Canadense
    'A$': 'AUD',     // Dólar Australiano
  };

  useEffect(() => {
    loadCurrencies();
  }, []);

  useEffect(() => {
    if (userData?.currency?.symbol) {
      setCurrentSymbol(userData.currency.symbol);
    } else {
      setCurrentSymbol('$');
    }
  }, [userData, currencies]);

  const loadCurrencies = async () => {
    try {
      const response = await API()({
        url: '/currencies',
        method: 'GET',
      });
      const currenciesData = Array.isArray(response) ? response : (response?.data || []);
      // Garantir que apenas moedas válidas sejam armazenadas
      const validCurrencies = (currenciesData || []).filter(
        currency => currency && currency.symbol && currency.code && currency.id
      );
      setCurrencies(validCurrencies);
    } catch (error) {
      console.error('Erro ao carregar moedas:', error);
      // Em caso de erro, definir array vazio para evitar crashes
      setCurrencies([]);
    }
  };

  // Agrupar moedas por símbolo e criar display com exemplos
  const getGroupedSymbols = (currenciesList = []) => {
    try {
      const groups = {};
      
      // Adicionar opção padrão ($)
      groups['$'] = {
        symbol: '$',
        id: null,
        displayName: '$ (Padrão)',
        examples: [],
        isDefault: true,
      };

      // Agrupar moedas por símbolo
      // Filtrar apenas moedas válidas antes de processar
      const validCurrencies = (currenciesList || []).filter(
        currency => currency && currency.symbol && currency.code && currency.id
      );

    validCurrencies.forEach(currency => {
      const symbol = currency.symbol;
      
      if (!groups[symbol]) {
        groups[symbol] = {
          symbol: symbol,
          id: null, // Será definido pela moeda prioritária
          displayName: symbol,
          examples: [],
          codes: [],
        };
      }
      
      // Garantir que examples e codes são arrays antes de fazer push
      if (!Array.isArray(groups[symbol].examples)) {
        groups[symbol].examples = [];
      }
      if (!Array.isArray(groups[symbol].codes)) {
        groups[symbol].codes = [];
      }
      
      // Adicionar exemplo (código da moeda)
      groups[symbol].examples.push(currency.code);
      groups[symbol].codes.push(currency.code);
      
      // Definir ID prioritário se for a moeda principal
      const priorityCode = SYMBOL_PRIORITY[symbol];
      if (priorityCode && currency.code === priorityCode) {
        groups[symbol].id = currency.id;
      } else if (!groups[symbol].id) {
        // Se não tem prioridade definida, usar a primeira encontrada
        groups[symbol].id = currency.id;
      }
    });

    // Formatar display name com exemplos
    Object.keys(groups).forEach(symbol => {
      if (!groups[symbol].isDefault && groups[symbol].examples.length > 0) {
        const examples = groups[symbol].examples.slice(0, 3).join(', '); // Mostrar até 3 exemplos
        const more = groups[symbol].examples.length > 3 ? '...' : '';
        groups[symbol].displayName = `${symbol} → ${examples}${more}`;
      }
    });

      const sortedGroups = Object.values(groups).sort((a, b) => {
        // Padrão ($) sempre primeiro
        if (a.isDefault) return -1;
        if (b.isDefault) return 1;
        
        // R$ deve aparecer em segundo lugar (logo após o padrão $)
        // Se a é R$ e b não é padrão, a vem antes
        if (a.symbol === 'R$' && !b.isDefault) return -1;
        // Se b é R$ e a não é padrão, b vem antes
        if (b.symbol === 'R$' && !a.isDefault) return 1;
        
        // € (Euro) deve aparecer em terceiro lugar (logo após R$)
        // Se a é € e b não é padrão nem R$, a vem antes
        if (a.symbol === '€' && !b.isDefault && b.symbol !== 'R$') return -1;
        // Se b é € e a não é padrão nem R$, b vem antes
        if (b.symbol === '€' && !a.isDefault && a.symbol !== 'R$') return 1;
        
        // Para os demais, ordenar alfabeticamente
        return a.symbol.localeCompare(b.symbol);
      });
      
      return sortedGroups;
    } catch (error) {
      console.error('Erro ao agrupar símbolos:', error);
      // Retornar apenas a opção padrão em caso de erro
      return [{
        symbol: '$',
        id: null,
        displayName: '$ (Padrão)',
        examples: [],
        isDefault: true,
      }];
    }
  };

  const handleCurrencyChange = async (symbol) => {
    try {
      const safeCurrencies = Array.isArray(currencies) ? currencies : [];
      const groupedSymbols = getGroupedSymbols(safeCurrencies);
      const selectedGroup = groupedSymbols.find(g => g && g.symbol === symbol);
      
      if (!selectedGroup) return;

      // Se for o padrão ($), enviar null
      // Caso contrário, enviar o ID da moeda prioritária
      const currencyIdToSend = selectedGroup.isDefault ? null : selectedGroup.id;
      
      await API()({
        url: '/user',
        method: 'PUT',
        data: { currency_id: currencyIdToSend },
      });

      setCurrentSymbol(symbol);

      // Encontrar a moeda completa para callback
      const fullCurrency = selectedGroup.isDefault 
        ? null 
        : safeCurrencies.find(c => c && c.id === selectedGroup.id);

      if (onCurrencyChange) {
        onCurrencyChange(fullCurrency);
      }

      // Disparar evento para atualizar formatação de moeda
      window.dispatchEvent(new CustomEvent('currencyChanged', { 
        detail: { currency: fullCurrency || { symbol } }
      }));
      
      // Invalidar cache de moeda
      if (typeof require !== 'undefined') {
        try {
          const { invalidateCurrencyCache } = require('../../utils/formaters');
          invalidateCurrencyCache();
        } catch (e) {
          // Ignorar erro
        }
      }

      // Disparar evento para forçar atualização da tela antes de mostrar o modal
      window.dispatchEvent(new CustomEvent('currencyFormatUpdate'));

      // Mostrar modal de sucesso após um pequeno delay para garantir que a atualização aconteceu
      setTimeout(() => {
        store.dispatch(
          applicationActions.toggleDialog({
            type: 'success',
            title: intl.formatMessage({ id: 'currency.update.success.title' }) || 'Moeda Atualizada',
            message: intl.formatMessage({ id: 'currency.update.success' }) || 'O símbolo monetário foi atualizado com sucesso. Todas as telas serão atualizadas automaticamente.',
            hasTimeout: true,
          })
        );
      }, 100);
    } catch (error) {
      console.error('Erro ao atualizar símbolo monetário:', error);
      store.dispatch(
        applicationActions.toggleDialog({
          type: 'error',
          title: intl.formatMessage({ id: 'currency.update.error.title' }) || 'Erro',
          message: intl.formatMessage({ id: 'currency.update.error' }) || 'Erro ao atualizar símbolo monetário',
          hasTimeout: true,
        })
      );
    }
  };

  // Garantir que currencies é sempre um array válido
  const safeCurrencies = Array.isArray(currencies) ? currencies : [];
  
  // Tentar renderizar apenas se tiver moedas carregadas
  let groupedSymbols = [];
  try {
    if (safeCurrencies.length > 0) {
      groupedSymbols = getGroupedSymbols(safeCurrencies);
    } else {
      // Se não tiver moedas ainda, não renderizar o componente
      return null;
    }
  } catch (error) {
    console.error('Erro ao preparar símbolos agrupados:', error);
    return null;
  }

  if (!Array.isArray(groupedSymbols) || groupedSymbols.length === 0) {
    return null;
  }

  return (
    <DropdownItem
      tag="span"
      style={{ cursor: 'default', padding: '0.5rem 1rem' }}
    >
      <FaDollarSign size={14} className="mr-50" />
      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        <span>Símbolo Monetário:</span>
        <select
          value={currentSymbol}
          onChange={(e) => {
            handleCurrencyChange(e.target.value);
          }}
          style={{
            border: '1px solid #ddd',
            borderRadius: '4px',
            padding: '4px 8px',
            background: 'white',
            fontWeight: 'bold',
            cursor: 'pointer',
            outline: 'none',
            marginLeft: '8px',
            minWidth: '200px',
            fontSize: '13px',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {groupedSymbols.map((group) => (
            <option key={group.symbol} value={group.symbol}>
              {group.displayName}
            </option>
          ))}
        </select>
      </span>
    </DropdownItem>
  );
};

export default CurrencyDropdownMenu;
